/**
 * Utilitários para extração de informações básicas da propriedade
 */

const resolvePropertiesRoot = (propertyData) => {
  if (!propertyData || typeof propertyData !== 'object') return {};

  if (propertyData.gn_properties && typeof propertyData.gn_properties === 'object') {
    return propertyData.gn_properties;
  }

  if (propertyData.properties && typeof propertyData.properties === 'object') {
    return propertyData.properties;
  }

  if (propertyData.data && typeof propertyData.data === 'object') {
    if (propertyData.data.gn_properties && typeof propertyData.data.gn_properties === 'object') {
      return propertyData.data.gn_properties;
    }
    if (propertyData.data.properties && typeof propertyData.data.properties === 'object') {
      return propertyData.data.properties;
    }
  }

  return {};
};

/**
 * Função auxiliar para extrair o nome da propriedade de acordo com propertyData.ts
 * @param {Object} propertyData - Dados da propriedade diretamente do JSON do backend
 * @returns {string} Nome da propriedade ou texto padrão
 */
export const extractPropertyName = (propertyData) => {
  try {
    const properties = resolvePropertiesRoot(propertyData);
    if (!properties || Object.keys(properties).length === 0) return 'Imóvel Rural';

    // Lógica de prioridade para nome da propriedade:
    // 1. SIGEF.details[].nome_da_propriedade
    // 2. SNCR.titulares[].nome_da_propriedade
    // 3. CAFIR.imoveis[].nome_da_propriedade
    // 4. Fallbacks anteriores

    const propertiesMap = properties;
    // Busca case-insensitive por chaves
    const getKeyCI = (obj, target) =>
      Object.keys(obj || {}).find((k) => k.toLowerCase() === target);

    // Prioridade especial: serpro_ccir.denominacao
    const ccirKey = getKeyCI(propertiesMap, 'serpro_ccir');
    if (ccirKey) {
      const ccirVal = propertiesMap[ccirKey];
      if (ccirVal) {
        if (Array.isArray(ccirVal)) {
          for (const item of ccirVal) {
            if (item?.denominacao) return item.denominacao;
          }
        } else if (typeof ccirVal === 'object') {
          if (ccirVal.denominacao) return ccirVal.denominacao;
        }
      }
    }

    // Fallback seguinte: SNCR.nome_da_propriedade (case-insensitive)
    const sncrKeyCI = getKeyCI(propertiesMap, 'sncr');
    if (sncrKeyCI && propertiesMap[sncrKeyCI]?.nome_da_propriedade) {
      return propertiesMap[sncrKeyCI].nome_da_propriedade;
    }

    // Fallback seguinte: CAFIR.nome_da_propriedade (case-insensitive)
    const cafirKeyCI = getKeyCI(propertiesMap, 'cafir');
    if (cafirKeyCI) {
      const cafirVal = propertiesMap[cafirKeyCI];
      if (cafirVal?.nome_da_propriedade) return cafirVal.nome_da_propriedade;
      if (Array.isArray(cafirVal?.imoveis)) {
        for (const imv of cafirVal.imoveis) {
          if (imv?.nome_da_propriedade) return imv.nome_da_propriedade;
        }
      }
    }

    // Fallback seguinte: SIGEF.nome_da_propriedade (case-insensitive)
    const sigefKeyCI = getKeyCI(propertiesMap, 'sigef');
    if (sigefKeyCI) {
      const sigefVal = propertiesMap[sigefKeyCI];
      if (Array.isArray(sigefVal)) {
        for (const s of sigefVal) {
          if (Array.isArray(s?.details)) {
            for (const d of s.details) {
              if (d?.nome_da_propriedade) return d.nome_da_propriedade;
            }
          }
          if (s?.nome_da_propriedade) return s.nome_da_propriedade;
          if (s?.imovel_descricao) return s.imovel_descricao;
          if (s?.denominacao) return s.denominacao;
        }
      } else if (sigefVal && typeof sigefVal === 'object') {
        if (Array.isArray(sigefVal.details)) {
          for (const d of sigefVal.details) {
            if (d?.nome_da_propriedade) return d.nome_da_propriedade;
          }
        }
        if (sigefVal?.nome_da_propriedade) return sigefVal.nome_da_propriedade;
        if (sigefVal?.imovel_descricao) return sigefVal.imovel_descricao;
        if (sigefVal?.denominacao) return sigefVal.denominacao;
      }
    }

    // 1. Verificar SIGEF (primeira prioridade)
    if (propertiesMap.sigef) {
      // Se for um array de parcelas SIGEF
      if (Array.isArray(propertiesMap.sigef)) {
        for (const sigefItem of propertiesMap.sigef) {
          // Verificar details das parcelas SIGEF
          if (Array.isArray(sigefItem.details) && sigefItem.details.length > 0) {
            for (const detail of sigefItem.details) {
              if (detail.nome_da_propriedade) {
                return detail.nome_da_propriedade;
              }
            }
          }
          // Tentar outros campos caso não exista details[].nome_da_propriedade
          if (sigefItem.nome_da_propriedade) return sigefItem.nome_da_propriedade;
          if (sigefItem.imovel_descricao) return sigefItem.imovel_descricao;
          if (sigefItem.denominacao) return sigefItem.denominacao;
        }
      }
      // Se for um objeto único (não array)
      else if (propertiesMap.sigef && typeof propertiesMap.sigef === 'object') {
        if (Array.isArray(propertiesMap.sigef.details) && propertiesMap.sigef.details.length > 0) {
          for (const detail of propertiesMap.sigef.details) {
            if (detail.nome_da_propriedade) return detail.nome_da_propriedade;
          }
        }
        if (propertiesMap.sigef.nome_da_propriedade) return propertiesMap.sigef.nome_da_propriedade;
        if (propertiesMap.sigef.imovel_descricao) return propertiesMap.sigef.imovel_descricao;
        if (propertiesMap.sigef.denominacao) return propertiesMap.sigef.denominacao;
      }
    }

    // Verificar em sigefparcel também (estrutura alternativa)
    if (propertiesMap.sigefparcel) {
      if (Array.isArray(propertiesMap.sigefparcel)) {
        for (const parcel of propertiesMap.sigefparcel) {
          if (parcel.nome_da_propriedade) return parcel.nome_da_propriedade;
        }
      } else if (typeof propertiesMap.sigefparcel === 'object') {
        if (propertiesMap.sigefparcel.nome_da_propriedade)
          return propertiesMap.sigefparcel.nome_da_propriedade;
      }
    }

    // 2. Verificar SNCR (segunda prioridade)
    if (propertiesMap.sncr) {
      // Verificar titulares
      if (Array.isArray(propertiesMap.sncr.titulares) && propertiesMap.sncr.titulares.length > 0) {
        for (const titular of propertiesMap.sncr.titulares) {
          if (titular.nome_da_propriedade) return titular.nome_da_propriedade;
        }
      }

      // Verificar outros campos do SNCR caso titular não tenha o nome
      if (propertiesMap.sncr.nome_da_propriedade) return propertiesMap.sncr.nome_da_propriedade;
      if (propertiesMap.sncr.nome_imovel) return propertiesMap.sncr.nome_imovel;
    }

    // Verificar em sncrdata também (estrutura alternativa)
    if (propertiesMap.sncrdata) {
      if (Array.isArray(propertiesMap.sncrdata)) {
        for (const item of propertiesMap.sncrdata) {
          if (item.nome_da_propriedade) return item.nome_da_propriedade;
        }
      } else if (typeof propertiesMap.sncrdata === 'object') {
        if (propertiesMap.sncrdata.nome_da_propriedade)
          return propertiesMap.sncrdata.nome_da_propriedade;
      }
    }

    // 3. Verificar CAFIR (terceira prioridade)
    if (propertiesMap.cafir) {
      // Verificar estrutura imoveis
      if (Array.isArray(propertiesMap.cafir.imoveis) && propertiesMap.cafir.imoveis.length > 0) {
        for (const imovel of propertiesMap.cafir.imoveis) {
          if (imovel.nome_da_propriedade) return imovel.nome_da_propriedade;
        }
      }

      // Verificar outros campos do CAFIR
      if (propertiesMap.cafir.nome_da_propriedade) return propertiesMap.cafir.nome_da_propriedade;
      if (propertiesMap.cafir.nome_imovel) return propertiesMap.cafir.nome_imovel;
    }

    // Verificar em cafirdata também (estrutura alternativa)
    if (propertiesMap.cafirdata) {
      if (Array.isArray(propertiesMap.cafirdata)) {
        for (const item of propertiesMap.cafirdata) {
          if (item.nome_da_propriedade) return item.nome_da_propriedade;
        }
      } else if (typeof propertiesMap.cafirdata === 'object') {
        if (propertiesMap.cafirdata.nome_da_propriedade)
          return propertiesMap.cafirdata.nome_da_propriedade;
      }
    }

    // Fallbacks anteriores para manter compatibilidade

    // Verificar CAR
    if (propertiesMap.car && Array.isArray(propertiesMap.car) && propertiesMap.car.length > 0) {
      const carItem = propertiesMap.car[0];

      if (carItem.details && carItem.details.nome_do_imovel) {
        return carItem.details.nome_do_imovel;
      }

      if (carItem.nome_imovel) {
        return carItem.nome_imovel;
      }
    }

    // Verificar SNCR (estrutura antiga)
    if (propertiesMap.SNCR) {
      if (propertiesMap.SNCR.nome_imovel) {
        return propertiesMap.SNCR.nome_imovel;
      }

      if (propertiesMap.SNCR.details) {
        if (Array.isArray(propertiesMap.SNCR.details) && propertiesMap.SNCR.details.length > 0) {
          const sncrDetail = propertiesMap.SNCR.details[0];
          if (sncrDetail && sncrDetail.nome_imovel) {
            return sncrDetail.nome_imovel;
          }
        } else if (propertiesMap.SNCR.details.nome_imovel) {
          return propertiesMap.SNCR.details.nome_imovel;
        }
      }
    }

    // Verificar SIGEF (estrutura antiga)
    if (
      propertiesMap.sigef &&
      Array.isArray(propertiesMap.sigef) &&
      propertiesMap.sigef.length > 0
    ) {
      const sigefItem = propertiesMap.sigef[0];

      if (sigefItem.imovel_descricao) {
        return sigefItem.imovel_descricao;
      }

      if (sigefItem.denominacao) {
        return sigefItem.denominacao;
      }
    }

    // Tentar usar o filename como última opção antes do default
    if (propertyData.filename) {
      // Remover extensão se houver
      return propertyData.filename.replace(/\.[^.]+$/, '');
    }

    return 'Imóvel Rural';
  } catch (error) {
    console.error('[propertyInfoExtractor] Erro ao extrair nome da propriedade:', error);
    return 'Imóvel Rural';
  }
};

/**
 * Função auxiliar para extrair município/UF diretamente dos dados JSON do backend
 * @param {Object} propertyData - Dados da propriedade diretamente do JSON do backend
 * @returns {string} Localização formatada (Município/UF) ou texto padrão
 */
export const extractLocation = (propertyData) => {
  try {
    const properties = resolvePropertiesRoot(propertyData);
    if (!properties || Object.keys(properties).length === 0) return 'Localização não disponível';

    // Ordem de prioridade para localização:
    // 1. car[0].details.municipio + car[0].details.uf
    // 2. SNCR.municipio + SNCR.uf
    // 3. sigef[0].municipio + sigef[0].uf

    let municipio = null;
    let uf = null;

    // Verificar CAR
    if (properties.car && Array.isArray(properties.car) && properties.car.length > 0) {
      const carItem = properties.car[0];

      if (carItem.details) {
        municipio = carItem.details.municipio || carItem.details.nome_municipio;
        uf = carItem.details.uf;

        if (municipio && uf) {
          return `${municipio}/${uf}`;
        }
      }
    }

    // Verificar SNCR
    if (properties.SNCR) {
      municipio = properties.SNCR.municipio || properties.SNCR.nome_municipio;
      uf = properties.SNCR.uf;

      if (municipio && uf) {
        return `${municipio}/${uf}`;
      }
    }

    // Verificar SIGEF
    if (properties.sigef && Array.isArray(properties.sigef) && properties.sigef.length > 0) {
      const sigefItem = properties.sigef[0];

      municipio = sigefItem.municipio_ || sigefItem.municipio || sigefItem.nome_municipio;
      uf = sigefItem.uf;

      if (municipio && uf) {
        return `${municipio}/${uf}`;
      }
    }

    return 'Localização não disponível';
  } catch (error) {
    console.error('[propertyInfoExtractor] Erro ao extrair localização:', error);
    return 'Localização não disponível';
  }
};
